import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AddEvent, CancelEvent, EditEvent, GridComponent, RemoveEvent, SaveEvent, SelectionEvent } from '@progress/kendo-angular-grid';
import { RaciStepTemplateModel } from '../../../models/raci-step-template-model';
import { RaciTaskTemplateModel } from '../../../models/raci-task-template-model';
import { RaciTemplateModel } from '../../../models/raci-template-model';
import { RaciTemplateService } from '../../../services/raci-template-service';
import { EventBusService } from '../../../../../services/event-bus.service';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';

/**
 * Handles raci template form component
 */
@Component({
  selector: 'raci-template-form',
  templateUrl: './raci-template-form.component.html',
  styleUrls: ['./raci-template-form.component.css']
})
export class RaciTemplateFormComponent implements OnInit {
  /**
  * The ID input property
  */
  @Input() id: number;

  /**
   * Close with refresh evet listener
   */
  @Output() closeWithRefresh: EventEmitter<any> = new EventEmitter();

  /**
  * The RACI template model
  */
  raciTemplate: RaciTemplateModel = new RaciTemplateModel();

  /**
   * The previous state of the RACI template model
   */
  previousStateRaciTemplate: RaciTemplateModel = new RaciTemplateModel();

  /**
   * The list of steps in the RACI template
   */
  steps: RaciStepTemplateModel[] = [];

  /**
   * The list of all tasks in the RACI template
   */
  allTasks: RaciTaskTemplateModel[] = [];

  /**
   * The list of tasks per step in the RACI template
   */
  tasksPerStep: RaciTaskTemplateModel[] = [];

  /**
   * The index of the currently edited row in the grid
   */
  private editedRowIndex: number;

  /**
   * The ID of the selected step
   */
  selectedStepId: number;

  /**
   * The ID of the selected task
   */
  selectedTaskId: number;

  /**
   * The selected indexes
   */
  selectedIndexes = [];

  /**
   * The selected task indexes
   */
  selectedTaskIndexes = [];

  /**
   * Flag indicating whether the template is new
   */
  isNewTemplate = false;

  /**
   * The configuration
   */
  configuration: any[] = [];

  /**
   * The selected configuration
   */
  selectedConfiguration = [];

  /**
   * Reference to the tasks grid component
   */
  @ViewChild("tasksGrid") taskGrid: GridComponent;

  /**
   * Reference to the steps grid component
   */
  @ViewChild("stepsGrid") stepsGrid: GridComponent;

  /**
   * The form group
   */
  formGroup: UntypedFormGroup;

  /**
   * Constructor
   * @param rtService
   * @param eventBus
   */
  constructor(private rtService: RaciTemplateService, private eventBus: EventBusService) { }

  /**
 * The template status list
 */
  public templateStatusList: Array<{ text: string; value: number }> = [
    { text: "Default", value: 0 },
    { text: "Active", value: 1 },
    { text: "Inactive", value: 2 },
  ];

  /**
   * The task types array
   */
  taskTypes = [];

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.createFormGroup();

    this.getTaskTypeData();

    // If edit, get template
    if (this.id >= 0) {
      this.rtService.get(this.id).subscribe(data => {
        this.raciTemplate = data;
        this.previousStateRaciTemplate = ObjectUtil.deepCopy(data);
        this.steps = data.steps;
        this.allTasks = data.tasks;
        this.stepsGrid.data = this.steps;
        this.selectedStepId = this.steps[0]?.oid;
        this.processTaskSteps(this.allTasks);
        this.tasksPerStep = this.allTasks.filter(s => s?.stepId === this.selectedStepId);
        this.taskGrid.data = this.tasksPerStep;

        this.steps.forEach(step => {
          step.tasks = this.allTasks.filter(task => task.stepId === step.oid);
        });
        this.previousStateRaciTemplate.steps = ObjectUtil.deepCopy(this.steps);
      });
    } else { // If new, set default values
      this.raciTemplate.status = 1;
      this.isNewTemplate = true;
    }
  }
  /**
   * Add new task for step
   */
  addNewTask(step: any) {
    /**
     * Create a new instance of FormGroup
     */
    this.formGroup = new UntypedFormGroup({
      /**
       * Create a new FormControl for the title field with required validation
       */
      title: new UntypedFormControl("", Validators.required),

      /**
       * Create a new FormControl for the description field with required validation
       */
      description: new UntypedFormControl("", Validators.required),

      /**
       * Create a new FormControl for the typeId field
       */
      typeId: new UntypedFormControl(""),

      /**
       * Create a new FormControl for the stepId field and initialize it with the value of step?.oid
       */
      stepId: new UntypedFormControl(step?.oid),

      /**
       * Create a new FormControl for the isNew field and initialize it with the value of true
       */
      isNew: new UntypedFormControl(true),

      /**
       * Create a new FormControl for the typeDescription field
       */
      typeDescription: new UntypedFormControl(""),

      /**
       * Create a new FormControl for the oid field
       */
      oid: new UntypedFormControl("")
    });
    this.taskGrid.addRow(this.formGroup);
    if (this.selectedStepId !== step.oid) {
      this.selectedStepId = step.oid;
      this.selectedIndexes = [step.oid];
      this.tasksPerStep = this.allTasks.filter(t => t?.stepId === this.selectedStepId);
      this.taskGrid.data = this.tasksPerStep;
    }
    this.selectedTaskId = 0;
    this.selectedTaskIndexes = [0];
  }

  // Step grid commands

  /**
   * Add new row in step grid
   * @param args
   */
  public addStepHandler(args: AddEvent): void {
    this.closeEditor(args.sender);
    this.formGroup = new UntypedFormGroup({
      title: new UntypedFormControl("", Validators.required),
      description: new UntypedFormControl(""),
      isNew: new UntypedFormControl(true),
      isProjectLevel: new UntypedFormControl(false),
      oid: new UntypedFormControl("")
    });
    this.selectedStepId = 0;
    this.selectedIndexes = [0];
    args.sender.addRow(this.formGroup);
  }

  /**
   * Edit existing row in step grid
   * @param args
   */
  public editStepHandler(args: EditEvent): void {
    const { dataItem } = args;
    this.taskGrid.closeRow(this.editedRowIndex);
    this.closeEditor(args.sender);
    this.selectedStepId = dataItem?.oid;
    this.selectedIndexes = [dataItem?.oid];
    this.formGroup.patchValue(dataItem);
    this.editedRowIndex = args.rowIndex;
    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  /**
   * Cancel step grid command
   */
  public cancelStepHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
  }

  /**
   * Upsert row in step grid
   * @param param0
   */
  public saveStepHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    const raciStepTemplateModel: RaciStepTemplateModel = formGroup?.value;
    if (isNew) {
      raciStepTemplateModel.oid = this.steps?.length + 1;
      raciStepTemplateModel.relatedSteps = [];
      this.steps.push(raciStepTemplateModel);
    } else {
      const existingStep = this.steps.find(s => s.oid == raciStepTemplateModel.oid);
      existingStep.title = raciStepTemplateModel?.title;
      existingStep.description = raciStepTemplateModel?.description;
      existingStep.isProjectLevel = raciStepTemplateModel?.isProjectLevel;
    }

    this.selectedStepId = raciStepTemplateModel?.oid;
    this.selectedIndexes = [raciStepTemplateModel?.oid];
    sender.closeRow(rowIndex);
    sender.data = this.steps;
    if (isNew) {
      this.tasksPerStep = [];
      this.taskGrid.data = this.tasksPerStep;
      this.filterSelectedStepDependencies(this.steps);
    }
  }

  /**
   * Remove step
   * @param args
   */
  public removeStepHandler(args: RemoveEvent): void {
    if (this.isNewTemplate) {
      this.removeStep(args);
    } else {
      this.rtService.getCanStepBeDeleted(args.dataItem?.templateId).subscribe(data => {
        if (data) {
          this.removeStep(args);
        } else {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'stepDeleteInfo');
        }
      });
    }
  }

  /**
   * Update project level status of selected item
   * @param item Selected or updated item
   */
  public changeProjectLevelStatus(item: any) {
    item.isProjectLevel = !item.isProjectLevel;
    this.formGroup.controls['isProjectLevel'].setValue(item.isProjectLevel);
  }

  /**
   * Close current editor
   */
  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup.reset();
  }

  /**
   * Change task type
   * @param selectedItem Selected item
   */
  changeTaskType(selectedItem: any) {
    this.formGroup.controls.typeId.setValue(selectedItem);
  }

  /**
   * When step is selected
   * @param e
   */
  public onStepSelect(e: SelectionEvent) {
    if (e.selectedRows.length > 0) {
      const item = e.selectedRows[0]?.dataItem;
      this.selectedStepId = item?.oid;
      this.selectedIndexes = [item?.oid];
      this.tasksPerStep = this.allTasks.filter(t => t.stepId === this.selectedStepId);
      this.taskGrid.data = this.tasksPerStep;
      this.filterSelectedStepDependencies(this.steps);
    }
  }

  /**
   * On task select
   * @param e
   */
  public onTaskSelect(e: SelectionEvent) {
    if (e.selectedRows.length > 0) {
      const item = e.selectedRows[0]?.dataItem;
      this.selectedTaskId = item?.oid;
      const selectedTasksPerStep = this.tasksPerStep.filter(task => task.oid != this.selectedTaskId);
      this.filterSelectedTaskDependencies(selectedTasksPerStep);
    }
  }

  /**
   * Create new configuration
   * @param item Step or Task, depends on selected row
   */
  createNewConfiguration(item: any, isStep = false) {
    let selectedStep;
    let selectedTask;

    if (isStep) {
      selectedStep = this.steps.find(step => step.oid == this.selectedStepId);
    } else {
      selectedTask = this.allTasks.find(task => task.oid == this.selectedTaskId);
    }

    return {
      "oid": item.oid,
      "title": item.title,
      "dependency": isStep ? !!selectedStep.relatedSteps.find(stepID => stepID == item.oid) : !!selectedTask.relatedTasks.find(stepID => stepID == item.oid),
      "isStep": isStep
    }
  }

  /**
   * Change dependency status
   * @param item
   */
  changeDependencyStatus(item: any) {
    let selectedRow;
    let dependencyRows;

    if (item.isStep) {
      selectedRow = this.steps.find(step => step.oid == this.selectedStepId);
      dependencyRows = this.steps.find(step => step.oid == item.oid);

      if (!selectedRow.relatedSteps.find(item => item == dependencyRows.oid)) {
        selectedRow.relatedSteps.push(item.oid);
      } else {
        const index = selectedRow.relatedSteps.indexOf(item);
        selectedRow.relatedSteps.splice(index, 1);
      }
    } else {
      selectedRow = this.allTasks.find(task => task.oid == this.selectedTaskId);
      dependencyRows = this.allTasks.find(task => task.oid == item.oid);

      if (!selectedRow.relatedTasks.find(item => item == dependencyRows.oid)) {
        selectedRow.relatedTasks.push(item.oid);
      } else {
        const index = selectedRow.relatedTasks.indexOf(item);
        selectedRow.relatedTasks.splice(index, 1);
      }
    }
  }

  /**
   * Create empty form gruop
   */
  private createFormGroup() {
    /**
  * Create a new instance of FormGroup
  */
    this.formGroup = new UntypedFormGroup({
      /**
       * Create a new FormControl for the title field with required validation
       */
      title: new UntypedFormControl("", Validators.required),

      /**
       * Create a new FormControl for the description field
       */
      description: new UntypedFormControl(""),

      /**
       * Create a new FormControl for the oid field
       */
      oid: new UntypedFormControl(""),

      /**
       * Create a new FormControl for the typeId field
       */
      typeId: new UntypedFormControl(""),

      /**
       * Create a new FormControl for the stepId field
       */
      stepId: new UntypedFormControl(""),

      /**
       * Create a new FormControl for the isProjectLevel field
       */
      isProjectLevel: new UntypedFormControl()
    });
  }

  /**
   * Task grid commands
   * @param args
   */
  public editTaskHandler(args: EditEvent): void {
    const { dataItem } = args;
    dataItem.isEdit = true;
    this.stepsGrid.closeRow(this.editedRowIndex);
    this.closeEditor(args.sender);
    this.formGroup.patchValue(dataItem);
    this.selectedTaskId = dataItem?.oid;
    this.selectedTaskIndexes = [dataItem?.oid];
    this.editedRowIndex = args.rowIndex;
    args.sender.editRow(args.rowIndex, this.formGroup);
  }

  /**
   * Cancel task handler
   * @param args
   */
  public cancelTaskHandler(args: CancelEvent): void {
    this.closeEditor(args.sender, args.rowIndex);
  }

  /**
   * Save task handler
   * @param param0
   */
  public saveTaskHandler({ sender, rowIndex, formGroup, isNew }: SaveEvent): void {
    const raciTaskTemplateModel: RaciTaskTemplateModel = formGroup.value;
    if (isNew) {
      raciTaskTemplateModel.oid = this.allTasks.length + 1;
      raciTaskTemplateModel.relatedTasks = [];
      this.tasksPerStep.push(raciTaskTemplateModel);
      this.allTasks.push(raciTaskTemplateModel);
    } else {
      const existingStep = this.tasksPerStep.find(s => s.oid == raciTaskTemplateModel.oid);
      existingStep.title = raciTaskTemplateModel?.title;
      existingStep.description = raciTaskTemplateModel?.description;
      existingStep.typeId = raciTaskTemplateModel.typeId;
      existingStep.typeDescription = this.taskTypes[existingStep.typeId]?.description;
    }
    raciTaskTemplateModel.typeDescription = this.taskTypes[raciTaskTemplateModel.typeId]?.description ? this.taskTypes[raciTaskTemplateModel.typeId]?.description : null;
    this.selectedTaskIndexes = [raciTaskTemplateModel.oid];
    sender.closeRow(rowIndex);
    sender.data = this.tasksPerStep;
    if (isNew) {
      this.selectedTaskId = raciTaskTemplateModel.oid;
      this.filterSelectedTaskDependencies(this.tasksPerStep);
    }
  }

  /**
   * Remove task handler
   * @param args
   */
  public removeTaskHandler(args: RemoveEvent): void {
    if (this.isNewTemplate) {
      this.removeTask(args);
    } else {
      this.rtService.getCanStepBeDeleted(args.dataItem.templateId).subscribe(data => {
        if (data) {
          this.removeTask(args);
        } else {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'taskDeleteInfo');
        }
      });
    }
  }

  /**
   * Performs an upsert operation for the template
   */
  upsertTemplate() {
    this.rtService.displayDataLoader(true);
    this.steps.forEach(step => {
      step.tasks = this.allTasks.filter(task => task.stepId === step.oid);
    })
    this.raciTemplate.steps = this.steps;
    this.raciTemplate.tasks = this.allTasks;
    if (this.compareTemplateStates(this.previousStateRaciTemplate, this.raciTemplate)) {
      this.rtService.update(this.raciTemplate).subscribe(() => {
        this.closeWithRefresh.emit();
        this.rtService.displayDataLoader(false);
      });
    } else {
      this.rtService.save(this.raciTemplate).subscribe(() => {
        this.closeWithRefresh.emit();
        this.rtService.displayDataLoader(false);
      });
    }
  }

  /**
   * Remove step
   * @param args Remove event
   */
  removeStep(args: RemoveEvent): void {
    args.dataItem.relatedSteps = [];
    this.steps.forEach(step => {
      step.relatedSteps = step.relatedSteps.filter(relatedStepId => relatedStepId != args.dataItem.oid);
    });
    this.steps = this.steps.filter(s => s.oid !== args.dataItem.oid);
    this.allTasks = this.allTasks.filter(t => t.stepId !== args.dataItem.oid);
    this.tasksPerStep = [];
    this.taskGrid.data = this.tasksPerStep;
    this.stepsGrid.data = this.steps;
  }

  /**
   * Remove task
   * @param args Remove event
   */
  removeTask(args: RemoveEvent): void {
    args.dataItem.relatedTasks = [];
    this.allTasks.forEach(task => {
      task.relatedTasks = task.relatedTasks?.filter(relatedTaskId => relatedTaskId != args.dataItem.oid);
    });
    const deletedTask = this.allTasks.find(t => t.oid === args.dataItem.oid);
    const index = this.allTasks.indexOf(deletedTask, 0);
    this.allTasks.splice(index, 1);
    this.tasksPerStep = this.allTasks.filter(t => t.stepId === this.selectedStepId);
    this.taskGrid.data = this.tasksPerStep;
  }

  /**
  *  Load task types
  */
  getTaskTypeData() {
    this.rtService.loadReflist('ref_task_type').subscribe((data: any) => {
      if (data) {
        this.taskTypes = data;
      }
    });
  }

  /**
   * Updates task type description
   * @param tasks
   */
  processTaskSteps(tasks: any) {
    tasks.forEach(task => {
      task.typeDescription = this.taskTypes[task.typeId]?.description;
    });
  }

  /**
   * Filters dependecies for one task (avoids circular dependencies)
   * @param tasksPerStep
   */
  filterSelectedTaskDependencies(tasksPerStep: any) {
    this.configuration = [];

    tasksPerStep.forEach(task => {
      if (task.oid != this.selectedTaskId) {
        if (task.relatedTasks.includes(this.selectedTaskId)) return;

        this.configuration.push(this.createNewConfiguration(task));
      }
    })
  }

  /**
   * Filters dependecies for one step (avoids circular dependencies)
   * @param steps
   */
  filterSelectedStepDependencies(steps: any) {
    this.configuration = [];

    steps.forEach(step => {
      if (step.oid != this.selectedStepId) {
        if (step.relatedSteps.includes(this.selectedStepId)) return;

        this.configuration.push(this.createNewConfiguration(step, true));
      }
    })
  }

  /**
   * Compares steps and tasks of two templates
   * @param previousState
   * @param updated
   * @returns
   */
  compareTemplateStates(previousState: RaciTemplateModel, updated: RaciTemplateModel) {
    return JSON.stringify(previousState.steps) === JSON.stringify(updated.steps);
  }
}
